import { useCallback, useEffect, useRef, useState } from 'react';
import useWorkflowStore from '@/stores/WorkflowStore';
import {
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from '@xyflow/react';
import * as d3Hierarchy from 'd3-hierarchy';
import { useShallow } from 'zustand/react/shallow';

import CustomEdge from '../CustomEdge';
import {
  ActionNode,
  BranchConfigNode,
  BranchDataNode,
  EndNode,
  TriggerNode,
} from '../CustomNodes';
import ActionConfiguration from './ActionConfiguration';
import TriggerConfiguration from './TriggerConfiguration';

import '@xyflow/react/dist/style.css';

import { toast } from 'sonner';

import CustomControls from '../CustomControls';

const nodeTypes = {
  trigger: (props) => <TriggerNode {...props} />,
  end: (props) => <EndNode {...props} />,
  action: (props) => <ActionNode {...props} />,
  'branch-config': (props) => <BranchConfigNode {...props} />,
  'branch-data': (props) => <BranchDataNode {...props} />,
};

const edgeTypes = {
  custom: CustomEdge,
};

const initialNodes = [
  {
    id: 'start',
    type: 'trigger',
    position: { x: 0, y: 0 },
    data: {
      label: 'Select a trigger',
      description: 'Choose what starts your workflow',
    },
  },
];

// Utility to calculate layout using d3-hierarchy
function calculateLayout(nodes, edges, options = {}) {
  const { direction = 'TB', nodeWidth = 180, nodeHeight = 60 } = options;

  if (!nodes || nodes.length === 0) return [];

  // Helper function to build a hierarchical structure from graph
  const buildHierarchy = () => {
    // Find the root node (usually the trigger)
    const rootNode = nodes.find((n) => n.type === 'trigger');
    if (!rootNode) return null;

    // Create a map of node id to its children
    const childrenMap = {};
    edges.forEach((edge) => {
      if (!childrenMap[edge.source]) {
        childrenMap[edge.source] = [];
      }
      childrenMap[edge.source].push(edge.target);
    });

    // Build the hierarchy recursively
    const buildNode = (nodeId) => {
      const node = nodes.find((n) => n.id === nodeId);
      if (!node) return null;

      // Get children for this node
      const childIds = childrenMap[nodeId] || [];
      const children = childIds.map((id) => buildNode(id)).filter(Boolean);

      // Return hierarchical node
      return {
        id: nodeId,
        type: node.type,
        data: node.data,
        width: node.measured?.width || node.width || nodeWidth,
        height: node.measured?.height || node.height || nodeHeight,
        originalNode: node,
        children: children,
      };
    };

    return buildNode(rootNode.id);
  };

  // Build the hierarchy
  const hierarchyRoot = buildHierarchy();
  if (!hierarchyRoot) return nodes; // Return original nodes if couldn't build hierarchy

  // Create hierarchy tree
  const root = d3Hierarchy.hierarchy(hierarchyRoot);

  // Create tree layout based on direction
  const treeLayout = d3Hierarchy
    .tree()
    .nodeSize([
      direction === 'TB' ? nodeWidth * 1.5 : nodeHeight * 2,
      direction === 'TB' ? nodeHeight * 4.75 : nodeWidth * 1.5,
    ])
    .separation((a, b) => {
      // Special handling for branch-data nodes to preserve their order
      if (
        a.parent === b.parent &&
        (a.data.type === 'branch-data' || b.data.type === 'branch-data')
      ) {
        return 1.5;
      }

      // Add extra spacing if parent is a trigger node
      if (a.parent && a.parent.data.type === 'trigger') {
        return 1.5; // Increased separation for trigger node children
      }

      return a.parent === b.parent ? 1 : 2;
    });

  // Apply the layout
  treeLayout(root);

  // Create a map for quick lookup
  const positionMap = {};
  root.descendants().forEach((node) => {
    positionMap[node.data.id] = {
      x: direction === 'TB' ? node.x : node.y,
      y: direction === 'TB' ? node.y : node.x,
    };
  });

  // Apply calculated positions to original nodes
  const laidOutNodes = nodes.map((node) => {
    const position = positionMap[node.id];
    if (!position) {
      return node; // Keep original position if not in the hierarchy
    }

    // Center the node on the calculated position
    const width = node.measured?.width || node.width || nodeWidth;
    const height = node.measured?.height || node.height || nodeHeight;

    return {
      ...node,
      position: {
        x: position.x - width / 2,
        y: position.y - height / 2,
      },
      width,
      height,
    };
  });

  return laidOutNodes;
}

const Flow = () => {
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const { fitView } = useReactFlow();
  const { setTempNodes, setTempEdges } = useWorkflowStore(
    useShallow((state) => ({
      setTempNodes: state.setTempNodes,
      setTempEdges: state.setTempEdges,
    }))
  );
  // Remove previous count refs if they are only used in the removed useEffect
  // const previousNodeCount = useRef(nodes.length);
  // const previousEdgeCount = useRef(edges.length);

  // Keep refs to access latest state in runLayout
  const nodesRef = useRef(nodes);
  const edgesRef = useRef(edges);

  // Effect for initial fitView on mount
  useEffect(() => {
    // Add a small delay to ensure the React Flow viewport is ready
    setTimeout(() => {
      fitView({ duration: 200, padding: 0.1 });
    }, 100); // 100ms delay, adjust if needed
  }, [fitView]); // Dependency on fitView ensures it's available

  // Effect to sync with Zustand store
  useEffect(() => {
    nodesRef.current = nodes;
    edgesRef.current = edges;

    setTempNodes(nodes);
    setTempEdges(edges);
  }, [nodes, edges, setTempNodes, setTempEdges]);

  const [isTriggerModalOpen, setIsTriggerModalOpen] = useState(false);
  const [isConfigSheetOpen, setIsConfigSheetOpen] = useState(false);
  const [selectedTriggerNode, setSelectedTriggerNode] = useState(null);

  const [isActionTypeModalOpen, setIsActionTypeModalOpen] = useState(false);
  const [isActionConfigSheetOpen, setIsActionConfigSheetOpen] = useState(false);
  const [selectedActionNode, setSelectedActionNode] = useState(null);
  const [actionSourceNode, setActionSourceNode] = useState(null);

  const [isBranchConfigOpen, setIsBranchConfigOpen] = useState(false);

  // --- Define runLayout function ---
  const runLayout = useCallback(() => {
    // Use a short timeout to ensure layout runs after state updates are likely processed
    setTimeout(() => {
      const currentNodes = nodesRef.current;
      const currentEdges = edgesRef.current;
      const hasNodes = currentNodes.length > 0;
      // We still need to ensure nodes have dimensions before calculating
      const allNodesMeasured =
        hasNodes && currentNodes.every((n) => n.measured?.width);

      if (hasNodes && allNodesMeasured) {
        const laidOutNodes = calculateLayout(currentNodes, currentEdges);

        // Simple check to avoid unnecessary updates if layout is identical
        const positionsDiffer =
          JSON.stringify(
            currentNodes.map((n) => ({
              id: n.id,
              x: n.position.x,
              y: n.position.y,
            }))
          ) !==
          JSON.stringify(
            laidOutNodes.map((n) => ({
              id: n.id,
              x: n.position.x,
              y: n.position.y,
            }))
          );

        if (positionsDiffer) {
          setNodes(laidOutNodes);
          // Call fitView after the state update has likely been processed
          setTimeout(() => {
            fitView({ padding: 0.1, duration: 200 });
          }, 0); // Zero delay pushes it to the next event loop tick
        } else {
          console.log('Skipping layout update (positions are the same).');
        }
      } else if (hasNodes && !allNodesMeasured) {
        console.log(
          'Layout skipped: Not all nodes have measured dimensions yet.'
        );
      } else {
        console.log('Layout skipped: No nodes present.');
      }
    }, 50); // Delay might need adjustment
  }, [setNodes, fitView]); // Dependency on setNodes and fitView

  // --- Utility function to find the parent branch-config node ---
  const findParentBranchConfigNode = (branchDataNodeId) => {
    const incomingEdges = edgesRef.current.filter(
      (edge) => edge.target === branchDataNodeId
    );

    for (const edge of incomingEdges) {
      const sourceNode = nodesRef.current.find(
        (node) => node.id === edge.source
      );

      if (sourceNode && sourceNode.type === 'branch-config') {
        return sourceNode;
      }
    }

    toast.error('No branch configuration found');
    return null;
  };

  const handleTriggerNodeClick = useCallback((node) => {
    setSelectedTriggerNode(node);
    if (node.data.triggerType) {
      setIsConfigSheetOpen(true);
    } else {
      setIsTriggerModalOpen(true);
    }
  }, []);

  const handleActionNodeClick = useCallback((node) => {
    setSelectedActionNode(node);

    if (node.type === 'branch-config') {
      setIsBranchConfigOpen(true);
    } else if (node.type === 'action') {
      setIsActionConfigSheetOpen(true);
    }
  }, []);

  const onNodeClick = useCallback(
    (event, node) => {
      const nodeType = node.type;

      if (nodeType === 'trigger') {
        handleTriggerNodeClick(node);
      } else if (nodeType === 'action' || nodeType === 'branch-config') {
        handleActionNodeClick(node);
      } else if (node.type === 'branch-data') {
        const parentConfigNode = findParentBranchConfigNode(node.id);
        if (parentConfigNode) {
          handleActionNodeClick(parentConfigNode);
        }
      }
    },
    [handleTriggerNodeClick, handleActionNodeClick]
  );

  return (
    <>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodeClick={onNodeClick}
        nodesDraggable={false}
        proOptions={{ hideAttribution: true }}
      >
        <CustomControls />
      </ReactFlow>

      <TriggerConfiguration
        isTriggerModalOpen={isTriggerModalOpen}
        setIsTriggerModalOpen={setIsTriggerModalOpen}
        isConfigSheetOpen={isConfigSheetOpen}
        setIsConfigSheetOpen={setIsConfigSheetOpen}
        selectedTriggerNode={selectedTriggerNode}
        setSelectedTriggerNode={setSelectedTriggerNode}
        setIsActionTypeModalOpen={setIsActionTypeModalOpen}
        setActionSourceNode={setActionSourceNode}
        nodes={nodes}
        setNodes={setNodes}
        edges={edges}
        setEdges={setEdges}
        runLayout={runLayout}
      />

      <ActionConfiguration
        isActionTypeModalOpen={isActionTypeModalOpen}
        setIsActionTypeModalOpen={setIsActionTypeModalOpen}
        isActionConfigSheetOpen={isActionConfigSheetOpen}
        setIsActionConfigSheetOpen={setIsActionConfigSheetOpen}
        selectedActionNode={selectedActionNode}
        setSelectedActionNode={setSelectedActionNode}
        actionSourceNode={actionSourceNode}
        setActionSourceNode={setActionSourceNode}
        isBranchConfigOpen={isBranchConfigOpen}
        setIsBranchConfigOpen={setIsBranchConfigOpen}
        nodes={nodes}
        setNodes={setNodes}
        edges={edges}
        setEdges={setEdges}
        runLayout={runLayout}
      />
    </>
  );
};

const WorkflowBuilder = () => {
  return (
    <div className='relative h-full w-full bg-background'>
      <ReactFlowProvider>
        <Flow />
      </ReactFlowProvider>
    </div>
  );
};

export default WorkflowBuilder;
